import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types';
import {
  login as apiLogin,
  register as apiRegister,
  verifyEmail as apiVerifyEmail,
  resendVerification as apiResendVerification,
  recoverVerification as apiRecoverVerification,
  logout as apiLogout,
  getCurrentUser,
} from '../services/auth.service';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: () => Promise<void>;
  microsoftLogin: () => Promise<void>;
  handleOAuthCallback: (token: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<{ email: string; pendingId: string }>;
  verifyEmailCode: (pendingId: string, code: string) => Promise<void>;
  resendVerificationCode: (pendingId: string) => Promise<void>;
  recoverVerification: (email: string, password: string) => Promise<{ pendingId: string; message: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  error: null,
  login: async () => {},
  googleLogin: async () => {},
  microsoftLogin: async () => {},
  handleOAuthCallback: async () => {},
  register: async () => ({ email: '', pendingId: '' }),
  verifyEmailCode: async () => {},
  resendVerificationCode: async () => {},
  recoverVerification: async () => ({ pendingId: '', message: '' }),
  logout: () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (token) {
          const userData = await getCurrentUser();
          setUser(userData);
        }
      } catch {
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { user, token } = await apiLogin(email, password);
      
      if (!user) {
        throw new Error('Invalid login response: user data missing');
      }
      
      if (!user.role) {
        user.role = 'user';
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('userRole', user.role);
      setUser(user);
    } catch (err: any) {
      setError(err.message || 'Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    setLoading(true);
    setError(null);
    
    try {
      window.location.href = '/api/auth/google';
    } catch (err: any) {
      setError(err.message || 'Failed to login with Google');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const microsoftLogin = async () => {
    setLoading(true);
    setError(null);
    
    try {
      window.location.href = '/api/auth/microsoft';
    } catch (err: any) {
      setError(err.message || 'Failed to login with Microsoft');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthCallback = async (token: string) => {
    setLoading(true);
    setError(null);
    
    try {
      localStorage.setItem('token', token);
      const userData = await getCurrentUser();
      
      if (!userData) {
        throw new Error('Failed to fetch user data');
      }
      
      localStorage.setItem('userRole', userData.role || 'user');
      setUser(userData);
    } catch (err: any) {
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      setError(err.message || 'OAuth authentication failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiRegister(name, email, password);
      if (!response.pending || !response.email || !response.pendingId) {
        throw new Error('Invalid registration response: verification is required');
      }
      return {
        email: response.email.trim().toLowerCase(),
        pendingId: response.pendingId,
      };
    } catch (err: any) {
      setError(err.message || 'Failed to register');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailCode = async (pendingId: string, code: string) => {
    setLoading(true);
    setError(null);

    try {
      const { user, token } = await apiVerifyEmail(pendingId, code);
      if (!user || !token) {
        throw new Error('Invalid email verification response');
      }
      const role = user.role || 'user';
      user.role = role;
      localStorage.setItem('token', token);
      localStorage.setItem('userRole', role);
      localStorage.removeItem('pendingSignupEmail');
      localStorage.removeItem('pendingSignupId');
      setUser(user);
    } catch (err: any) {
      setError(err.message || 'Email verification failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationCode = async (pendingId: string) => {
    setLoading(true);
    setError(null);
    try {
      await apiResendVerification(pendingId);
    } catch (err: any) {
      setError(err.message || 'Could not resend the verification code');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const recoverVerification = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      return await apiRecoverVerification(email, password);
    } catch (err: any) {
      setError(err.message || 'Could not request a verification code');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      // Logout errors are suppressed to ensure clean client-side logout
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await getCurrentUser();
      if (userData) {
        setUser(userData);
      }
    } catch {
      // Silently fail - user may have been logged out
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    googleLogin,
    microsoftLogin,
    handleOAuthCallback,
    register,
    verifyEmailCode,
    resendVerificationCode,
    recoverVerification,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
